import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

const BreadcrumbContext = createContext({
  breadcrumbTrail: undefined,
  updateBreadcrumb: () => {},
  addToBreadcrumb: () => {},
  resetBreadcrumb: () => {},
  setBreadcrumb: () => {},
  navigateToCrumb: () => {},
  setStageBreadcrumb: () => {},
});
const BREADCRUMB_STORAGE_KEY = 'breadcrumbTrail';

function BreadcrumbProvider({ children }) {
  const [breadcrumbTrail, setBreadcrumbTrail] = useState([]);
  const [stageBreadcrumbTrail, setStageBreadcrumbTrail] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const saveToSession = (trail) => {
    if (trail.length === 0) {
      sessionStorage.removeItem(BREADCRUMB_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(BREADCRUMB_STORAGE_KEY, JSON.stringify(trail));
  };

  const generateFallback = () => {};

  useEffect(() => {
    const saved = sessionStorage.getItem(BREADCRUMB_STORAGE_KEY);
    if (saved) {
      setBreadcrumbTrail(JSON.parse(saved));
    } else {
      generateFallback(location.pathname, params);
    }
  }, []);

  const updateBreadcrumb = (newTrail) => {
    setBreadcrumbTrail(newTrail);
    saveToSession(newTrail);
  };

  const resetBreadcrumb = (newTrail) => {
    setBreadcrumbTrail(newTrail);
    saveToSession(newTrail);
  };

  const setBreadcrumb = (newTrail) => {
    setBreadcrumbTrail(newTrail);
    saveToSession(newTrail);
  };

  const replaceLastBreadCrumb = (trail) => {
    const newBreacrumb = breadcrumbTrail;
    newBreacrumb[breadcrumbTrail.length - 1] = trail;
    console.log('routeBreadcrumbs called here');
    return newBreacrumb;
  };

  const addToBreadcrumb = (additional) => {
    let newTrail = [];
    const lastTrail = breadcrumbTrail[breadcrumbTrail.length - 1];
    if (
      additional.length === 1 &&
      lastTrail?.subLabel === additional[0]?.subLabel
    ) {
      newTrail = replaceLastBreadCrumb(additional[0]);
    } else {
      newTrail = [...breadcrumbTrail, ...additional];
    }
    if (breadcrumbTrail.length === 0 && stageBreadcrumbTrail) {
      newTrail = [...stageBreadcrumbTrail, ...newTrail];
    }
    setBreadcrumbTrail(newTrail);
    saveToSession(newTrail);
  };

  const navigateToCrumb = (index, crumbList, newCrumb) => {
    if (breadcrumbTrail.length > 0) {
      const newTrail = breadcrumbTrail.slice(0, index + 1);
      setBreadcrumbTrail(index === 0 ? [] : newTrail);
      saveToSession(index === 0 ? [] : newTrail);
      const crumb = newCrumb ?? newTrail[index];
      navigate(crumb.path, {
        state: { routeBreadcrumbs: crumbList },
      });
    }
  };

  const setStageBreadcrumb = (trail) => {
    setStageBreadcrumbTrail(trail);
  };

  const contextValue = useMemo(
    () => ({
      breadcrumbTrail,
      updateBreadcrumb,
      addToBreadcrumb,
      resetBreadcrumb,
      navigateToCrumb,
      setBreadcrumb,
      setStageBreadcrumb,
    }),
    [breadcrumbTrail, stageBreadcrumbTrail],
  );

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export const useBreadcrumb = () => useContext(BreadcrumbContext);

BreadcrumbProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default BreadcrumbProvider;
